import React, { useEffect, useRef, useState } from "react";
import { PieChart, Pie, Cell, Sector, ResponsiveContainer } from "recharts";
import CustomLegend from "./CustomLegend";

/**
 * PieChartData represents a single slice of the pie chart.
 *
 * @typedef {Object} PieChartData
 * @property {string} name - Label for the pie segment
 * @property {number} value - Numeric value for the segment
 */
interface PieChartData {
  name: string;
  value: number;
}

interface PieChartContainerProps {
  data: PieChartData[];
}

const COLORS = [
  { background: "#1C359A" },
  { background: "#ABB5DC" },
  { background: "#455EC3" },
  { background: "#6E81D0" },
  { background: "#5081E6" },
  { background: "#10225B" },
  { background: "#DADFF2", color: "#6E81D0" },
  { background: "#CCE3F7", color: "#6E81D0" },
];

const RenderCustomSector = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props;

  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  );
};

const legendItems = [
  { name: "QC Pass", color: "#1C359A" },
  { name: "QC Fail", color: "#ABB5DC" },
  // { name: "Resolved Actions", color: "#6E81D0" },
];

/**
 * PieChartContainer renders a responsive pie chart using Recharts
 * to visualize categorized data with a custom legend and total label.
 *
 * It supports smooth rounded sectors, percentage padding, and dynamic sizing
 * using ResizeObserver to adjust inner and outer radius.
 *
 * @component
 * @param {Object} props
 * @param {PieChartData[]} props.data - Array of pie chart segments with names and values
 *
 * @example
 * const data = [
 *   { name: "Open Actions", value: 25 },
 *   { name: "Resolved Actions", value: 15 },
 * ];
 *
 * <PieChartContainer data={data} />
 */
const PieChartContainer: React.FC<PieChartContainerProps> = ({ data }) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setDimensions({ width, height });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const radiusBase = Math.min(dimensions.width, dimensions.height) / 2;
  const innerRadius = radiusBase * 0.6;
  const outerRadius = radiusBase * 0.8;
  return (
    <>
      <CustomLegend items={legendItems} itemsPerRow={1} />
      <div ref={containerRef} style={{ width: "100%", height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              fill="#8884d8"
              paddingAngle={5}
              dataKey="value"
              activeShape={RenderCustomSector}
              cornerRadius={10}
              labelLine={false}
              label={({ cx, cy, midAngle, outerRadius, index }) => {
                const RADIAN = Math.PI / 180;
                const radius = outerRadius * 1.15;
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);

                return (
                  <text
                    x={x}
                    y={y}
                    fill="#626262"
                    textAnchor="middle"
                    fontSize={14}
                    fontWeight={400}
                  >
                    {(
                      (data[index].value * 100) /
                      data.reduce((acc, item) => (acc += item.value), 0)
                    ).toFixed(2)}
                    %
                  </text>
                );
              }}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length].background}
                />
              ))}
            </Pie>
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={46}
              fill="#626262"
              fontWeight={500}
            >
              {data.reduce((acc, item) => acc + item.value, 0)}
            </text>
            <text
              x="50%"
              y="60%"
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={16}
              fill="#626262"
              fontWeight={400}
            >
              Total Reviews
            </text>
          </PieChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

export default PieChartContainer;
